/**
 * Shared product components for Sky sections.
 *
 * <sky-product-form>    variant picker, price, stock, quantity and coupon copy. Wraps the info card and
 *                       renders `sky-product-form.liquid` inside. Fires `sky:variant-change` (bubbles).
 * <sky-product-gallery> carousel (thumbs + arrows); with data-mode="stack" the slides are stacked on
 *                       desktop and the thumbs jump to each image.
 * <sky-sticky-atc>      bottom bar that appears once the main add-to-cart button scrolls out of view.
 *
 * Adding to cart is handled by sky-cart.js through `data-sky-product-form`.
 */

class SkyProductForm extends HTMLElement {
  connectedCallback() {
    this.variants = JSON.parse(this.querySelector('[data-product-json]')?.textContent || '[]');
    this.strings = Object.fromEntries(
      [...(this.querySelector('[data-strings]')?.content.children ?? [])].map((el) => [
        /** @type {HTMLElement} */ (el).dataset.key,
        el.textContent.trim(),
      ])
    );

    this.querySelector('.sky-fp__options')?.addEventListener('change', () => this.onOptionChange());

    this.querySelectorAll('[data-qty]').forEach((button) =>
      button.addEventListener('click', () => {
        const input = /** @type {HTMLInputElement} */ (this.querySelector('input[name="quantity"]'));
        input.value = String(Math.max(1, (Number(input.value) || 1) + Number(button.dataset.qty)));
      })
    );

    const discount = /** @type {HTMLDetailsElement | null} */ (this.querySelector('.sky-fp__discount'));
    discount?.querySelector('summary')?.addEventListener('click', (event) => {
      event.preventDefault();
      this.toggleDiscount(discount);
    });

    const coupon = /** @type {HTMLElement | null} */ (this.querySelector('[data-copy]'));
    coupon?.addEventListener('click', async () => {
      try {
        await navigator.clipboard.writeText(coupon.dataset.copy || '');
        coupon.querySelector('[data-copied]')?.removeAttribute('hidden');
      } catch {}
    });

    this.markUnavailable();

    // Sticky 20px under the header like Sky. A card taller than the screen scrolls along with
    // the page until its bottom (scrolling down) or its top (scrolling up) is reached.
    if (this.hasAttribute('data-smart-sticky')) {
      this.stickyTop = null;
      this.lastScroll = this.scrollPosition();
      this.onStickyScroll = () => this.updateStickyTop();
      this.resizeObserver = new ResizeObserver(this.onStickyScroll);
      this.resizeObserver.observe(this);
      window.addEventListener('resize', this.onStickyScroll, { passive: true });
      document.addEventListener('scroll', this.onStickyScroll, { passive: true, capture: true });
      this.updateStickyTop();
    }
  }

  disconnectedCallback() {
    this.resizeObserver?.disconnect();
    if (this.onStickyScroll) {
      window.removeEventListener('resize', this.onStickyScroll);
      document.removeEventListener('scroll', this.onStickyScroll, { capture: true });
    }
  }

  /** Horizon scrolls `.page-wrapper` on desktop and the document elsewhere. */
  scrollPosition() {
    return Math.max(window.scrollY, document.querySelector('.page-wrapper')?.scrollTop ?? 0);
  }

  /**
   * Opens/closes the discount banner with a height slide instead of the native jump.
   * @param {HTMLDetailsElement} details
   */
  toggleDiscount(details) {
    const body = /** @type {HTMLElement | null} */ (details.querySelector('.sky-fp__discount-body'));
    const opening = !details.open || details.classList.contains('is-closing');
    this.discountAnimation?.cancel();
    details.classList.toggle('is-closing', !opening);
    details.open = true;
    if (!body || window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      details.open = opening;
      details.classList.remove('is-closing');
      return;
    }
    const { paddingBottom } = getComputedStyle(body);
    const shown = { height: `${body.scrollHeight}px`, paddingBottom };
    const hidden = { height: '0px', paddingBottom: '0px' };
    this.discountAnimation = body.animate(opening ? [hidden, shown] : [shown, hidden], {
      duration: 600,
      easing: 'cubic-bezier(0.4, 0, 0.2, 1)',
    });
    this.discountAnimation.onfinish = () => {
      details.open = opening;
      details.classList.remove('is-closing');
    };
  }

  updateStickyTop() {
    const header = document.querySelector('.sky-header-section');
    const headerBottom = header ? Math.max(0, header.getBoundingClientRect().bottom) : 0;
    const max = headerBottom + 20;
    const min = Math.min(max, window.innerHeight - this.offsetHeight - 20);
    const scroll = this.scrollPosition();
    const delta = scroll - (this.lastScroll ?? scroll);
    this.lastScroll = scroll;
    const current = this.stickyTop ?? max;
    this.stickyTop = Math.min(max, Math.max(min, current - delta));
    this.style.top = `${this.stickyTop}px`;
  }

  get currentVariant() {
    const id = /** @type {HTMLInputElement | null} */ (this.querySelector('[data-variant-id]'))?.value;
    return this.variants.find((v) => String(v.id) === id);
  }

  selectedOptions() {
    return [...this.querySelectorAll('.sky-fp__option')].map((fieldset) => {
      const select = /** @type {HTMLSelectElement | null} */ (fieldset.querySelector('select'));
      if (select) return select.value;
      const checked = /** @type {HTMLInputElement | null} */ (fieldset.querySelector('input:checked'));
      return checked?.value ?? '';
    });
  }

  onOptionChange() {
    const selected = this.selectedOptions();
    this.querySelectorAll('.sky-fp__option').forEach((fieldset, i) => {
      const label = fieldset.querySelector('[data-option-value]');
      if (label) label.textContent = selected[i];
    });

    const variant = this.variants.find((v) => v.options.every((value, i) => value === selected[i]));
    this.update(variant);
    this.markUnavailable();
    this.dispatchEvent(new CustomEvent('sky:variant-change', { bubbles: true, detail: { variant } }));
  }

  /** @param {any} variant */
  update(variant) {
    const idInput = /** @type {HTMLInputElement} */ (this.querySelector('[data-variant-id]'));
    const add = /** @type {HTMLButtonElement} */ (this.querySelector('[data-add]'));
    const price = this.querySelector('[data-price]');
    const stock = this.querySelector('[data-stock]');

    if (!variant) {
      add.disabled = true;
      add.textContent = add.dataset.soldOut || this.strings.sold_out;
      return;
    }

    idInput.value = String(variant.id);
    add.disabled = !variant.available;
    add.textContent = variant.available
      ? add.dataset.label || this.strings.add
      : add.dataset.soldOut || this.strings.sold_out;

    if (price) {
      price.innerHTML = `<span>${variant.price}</span>${variant.compare ? `<s>${variant.compare}</s>` : ''}`;
    }

    if (stock) {
      stock.classList.toggle('is-out', !variant.available);
      stock.textContent = !variant.available
        ? this.strings.sold_out
        : variant.qty
          ? this.strings.in_stock_count.replace('[qty]', variant.qty)
          : this.strings.in_stock;
    }

    if (this.hasAttribute('data-update-url')) {
      const url = new URL(window.location.href);
      url.searchParams.set('variant', String(variant.id));
      window.history.replaceState({}, '', url);
    }
  }

  /** Strike through values that can't combine with the current selection into an available variant. */
  markUnavailable() {
    const selected = this.selectedOptions();
    this.querySelectorAll('.sky-fp__option').forEach((fieldset, optionIndex) => {
      fieldset.querySelectorAll('input, option').forEach((input) => {
        const available = this.variants.some(
          (v) =>
            v.available &&
            v.options[optionIndex] === /** @type {HTMLInputElement} */ (input).value &&
            v.options.every((value, i) => i === optionIndex || value === selected[i])
        );
        input.classList.toggle('is-unavailable', !available);
      });
    });
  }
}

class SkyProductGallery extends HTMLElement {
  connectedCallback() {
    this.slides = /** @type {HTMLElement} */ (this.querySelector('[data-slides]'));
    this.thumbs = [...this.querySelectorAll('[data-thumb]')];
    this.dots = [...this.querySelectorAll('[data-dot]')];
    this.track = /** @type {HTMLElement | null} */ (this.querySelector('[data-thumbs-track]'));
    this.stackQuery = window.matchMedia('(min-width: 1025px)');

    this.thumbs.forEach((thumb) => thumb.addEventListener('click', () => this.goTo(Number(thumb.dataset.thumb))));
    this.querySelectorAll('[data-prev]').forEach((b) => b.addEventListener('click', () => this.step(-1)));
    this.querySelectorAll('[data-next]').forEach((b) => b.addEventListener('click', () => this.step(1)));
    this.slides?.addEventListener('scroll', () => this.syncThumbs(), { passive: true });

    this.observeStack();
    if (this.hasAttribute('data-zoom')) this.setupZoom();

    this.closest('.shopify-section')?.addEventListener('sky:variant-change', (event) => {
      const media = /** @type {CustomEvent} */ (event).detail.variant?.media;
      if (!media || !this.slides) return;
      const index = [...this.slides.children].findIndex((s) => /** @type {HTMLElement} */ (s).dataset.mediaId === String(media));
      if (index > -1) this.goTo(index);
    });
  }

  disconnectedCallback() {
    this.observer?.disconnect();
  }

  /** Width of one slider column: slides may be narrower than it (centred), so don't use their own width. */
  get columnWidth() {
    if (!this.slides) return 1;
    return this.slides.clientWidth + (parseFloat(getComputedStyle(this.slides).columnGap) || 0);
  }

  /** Desktop "Stacked" and "2-column grid" layouts scroll the page instead of a slider. */
  get isStack() {
    return (this.dataset.mode === 'stack' || this.dataset.mode === 'grid') && this.stackQuery.matches;
  }

  get index() {
    if (!this.slides) return 0;
    if (this.isStack) return this.activeStackIndex ?? 0;
    return Math.round(this.slides.scrollLeft / (this.columnWidth || 1));
  }

  /** @param {number} direction */
  step(direction) {
    this.goTo(this.index + direction);
  }

  /** @param {number} i */
  goTo(i) {
    if (!this.slides) return;
    const count = this.slides.children.length;
    const index = (i + count) % count;
    const slide = /** @type {HTMLElement} */ (this.slides.children[index]);

    if (this.isStack) {
      slide.scrollIntoView({ behavior: 'smooth', block: 'start' });
    } else {
      this.slides.scrollTo({ left: index * this.columnWidth, behavior: 'smooth' });
    }
    this.setActiveThumb(index);
  }

  syncThumbs() {
    if (this.isStack) return;
    clearTimeout(this.syncTimer);
    this.syncTimer = setTimeout(() => this.setActiveThumb(this.index), 80);
  }

  /** Track which stacked image is in the middle of the screen. */
  observeStack() {
    this.observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (!entry.isIntersecting || !this.isStack) continue;
          this.activeStackIndex = [...this.slides.children].indexOf(entry.target);
          this.setActiveThumb(this.activeStackIndex);
        }
      },
      { rootMargin: '-45% 0px -45% 0px' }
    );
    [...this.slides.children].forEach((slide) => this.observer.observe(slide));
  }

  /** @param {number} index */
  setActiveThumb(index) {
    this.thumbs.forEach((thumb, i) => {
      thumb.classList.toggle('is-active', i === index);
      if (i === index) this.revealThumb(thumb);
    });
    this.dots.forEach((dot, i) => dot.classList.toggle('is-active', i === index));
  }

  /**
   * Scrolls only the thumbnail strip (never the page) so the active thumbnail is centred.
   * @param {HTMLElement} thumb
   */
  revealThumb(thumb) {
    const track = this.track;
    if (!track || !track.offsetParent) return;
    const vertical = track.scrollHeight > track.clientHeight + 1;
    if (vertical) {
      track.scrollTo({ top: thumb.offsetTop - track.offsetTop - (track.clientHeight - thumb.offsetHeight) / 2, behavior: 'smooth' });
    } else {
      track.scrollTo({ left: thumb.offsetLeft - track.offsetLeft - (track.clientWidth - thumb.offsetWidth) / 2, behavior: 'smooth' });
    }
  }

  /** Magnify the image under the pointer (desktop, fine pointer only). */
  setupZoom() {
    const canZoom = window.matchMedia('(min-width: 1025px) and (hover: hover) and (pointer: fine)');
    this.slides?.querySelectorAll('.sky-mp__slide').forEach((slide) => {
      const img = slide.querySelector('img');
      if (!img) return;
      slide.addEventListener('pointermove', (event) => {
        if (!canZoom.matches) return;
        const rect = slide.getBoundingClientRect();
        img.style.transformOrigin = `${((event.clientX - rect.left) / rect.width) * 100}% ${((event.clientY - rect.top) / rect.height) * 100}%`;
        slide.classList.add('is-zoomed');
      });
      slide.addEventListener('pointerleave', () => slide.classList.remove('is-zoomed'));
    });
  }
}

class SkyStickyAtc extends HTMLElement {
  connectedCallback() {
    const section = this.closest('.shopify-section');
    this.form = /** @type {HTMLFormElement | null} */ (section?.querySelector('form[data-sky-product-form]'));
    this.productForm = /** @type {SkyProductForm | null} */ (section?.querySelector('sky-product-form'));
    const mainButton = section?.querySelector('[data-add]');
    if (!this.form || !mainButton) return;

    // Show once the main button has scrolled away, or (desktop, where the info card stays pinned)
    // once the whole product area has been scrolled past.
    const wrap = section?.querySelector('.sky-fp__wrap');
    const passed = new Map();
    this.observer = new IntersectionObserver((entries) => {
      for (const entry of entries) {
        passed.set(entry.target, !entry.isIntersecting && entry.boundingClientRect.top < 0);
      }
      this.classList.toggle('is-visible', [...passed.values()].some(Boolean));
    });
    this.observer.observe(mainButton);
    if (wrap) this.observer.observe(wrap);

    this.querySelector('[data-sticky-add]')?.addEventListener('click', () => this.form?.requestSubmit());

    section?.addEventListener('sky:variant-change', (event) => this.render(/** @type {CustomEvent} */ (event).detail.variant));
  }

  disconnectedCallback() {
    this.observer?.disconnect();
  }

  /** @param {any} variant */
  render(variant) {
    if (!variant) return;
    const title = this.querySelector('[data-sticky-variant]');
    const price = this.querySelector('[data-sticky-price]');
    const image = /** @type {HTMLImageElement | null} */ (this.querySelector('[data-sticky-image]'));
    const button = /** @type {HTMLButtonElement | null} */ (this.querySelector('[data-sticky-add]'));
    const mainButton = this.closest('.shopify-section')?.querySelector('[data-add]');

    if (title) title.textContent = variant.title === 'Default Title' ? '' : variant.title.replaceAll(' / ', ' • ');
    if (price) price.innerHTML = `<span>${variant.price}</span>${variant.compare ? `<s>${variant.compare}</s>` : ''}`;
    if (image && variant.image) image.src = variant.image;
    if (button) {
      button.disabled = !variant.available;
      button.textContent = mainButton?.textContent?.trim() || button.textContent;
    }
  }
}

if (!customElements.get('sky-product-form')) customElements.define('sky-product-form', SkyProductForm);
if (!customElements.get('sky-product-gallery')) customElements.define('sky-product-gallery', SkyProductGallery);
if (!customElements.get('sky-sticky-atc')) customElements.define('sky-sticky-atc', SkyStickyAtc);
